<template>
  <div class="sales-table" v-if="projects">
    <DataTable
      class="p-datatable-sm p-datatable-striped p-datatable-gridlines"
      ref="salesTable"
      :value="projects"
      dataKey="number"
      :lazy="true"
      :loading="isLoading"
      :totalRecords="getProjectCount"
      :paginator="true"
      :rows.sync="tableStateStore.pagination.pageSize"
      :first="pageOffset"
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      :currentPageReportTemplate="
        $t('show') +
          ' {first} ' +
          $t('to') +
          ' {last} ' +
          $t('of') +
          ' {totalRecords} ' +
          $t('records')
      "
      :rowsPerPageOptions="[10, 15, 20, 25, 30, 40, 50, 100, 200]"
      :filters.sync="tableStateStore.filters"
      stateStorage="local"
      :stateKey="tableStateStore.filterName()"
      filterDisplay="row"
      @page="onPage($event)"
      @sort="onSort($event)"
      @filter="onFilter($event)"
      @state-restore="onStateRestore($event)"
      @cell-edit-complete="onCellEditComplete"
      :rowHover="true"
      :sortField="tableStateStore.sorting.sortField"
      :sortOrder="tableStateStore.sorting.sortOrder"
      responsiveLayout="scroll"
    >
      <template #header>
        <div class="table-header">
          <div class="table-header-left">
            <h4 class="page-title">{{ $t('sales') }}</h4>
          </div>
          <div class="table-header-right">
            <button class="btn btn-sm btn-inverse" @click.prevent="onRemoveFilters()">
              <i class="fa fa-remove mr-2"></i>
              {{ $t('remove_filters') }}
            </button>

            <router-link
              v-if="hasResourcePermission(getCurrentUser, 'SALES', ['CREATE'])"
              class="btn btn-sm btn-inverse"
              :to="{ name: 'SalesCreatePage' }"
            >
              <i class="fa fa-plus-circle mr-2"></i>
              {{ $t('sales_create') }}
            </router-link>
          </div>
        </div>
      </template>

      <template #empty>
        {{ $t('no_data_found') }}
      </template>
      <template #loading>
        {{ $t('loading') }}
      </template>

      <Column
        field="number"
        header="#"
        sortable
        :showFilterMenu="false"
        :headerStyle="{ width: '60px' }"
        :bodyStyle="{ 'text-align': 'center' }"
      >
        <template #body="slotProps">
          <router-link
            v-if="slotProps.data.number"
            :to="{
              name: 'SalesEditPage',
              params: { projectNumber: slotProps.data.number },
            }"
            >{{ slotProps.data.number.toString().padStart(4, '0') }}</router-link
          >
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <Column
        field="customer.lastname"
        :header="$t('lastname')"
        filterField="customerLastname"
        sortable
      >
        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <Column
        field="customer.firstname"
        :header="$t('firstname')"
        filterField="customerFirstname"
        sortable
      >
        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <Column
        field="customer.street"
        :header="$t('street')"
        filterField="customerStreet"
        sortable
        sortField="customer.street"
      >
        <template #body="slotProps">
          <a :href="generateGoogleMapsLink(slotProps.data.customer)" target="_blank">
            {{ slotProps.data.customer.street }}&nbsp;
            {{ slotProps.data.customer.streetNumber }}
            {{
              slotProps.data.customer.streetNumberSuffix
                ? slotProps.data.customer.streetNumberSuffix
                : ''
            }}
          </a>
        </template>
        <template #filter="{filterModel, filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <Column
        field="customer.zip"
        :header="$t('zip')"
        filterField="customerZip"
        sortable
        sortField="customer.zip"
        :headerStyle="{ width: '60px' }"
        :bodyStyle="{ 'text-align': 'center' }"
      >
        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <Column field="customer.city" :header="$t('city')" filterField="customerCity" sortable>
        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <Column field="customer.phone" :header="$t('phone')" filterField="customerPhone" sortable>
        <template #body="slotProps">
          <a
            @click.prevent="startTeamsCall(slotProps.data.customer.phone)"
            href="#"
            target="_blank"
          >
            {{ slotProps.data.customer.phone }}
          </a>
        </template>
        <template #filter="{filterModel, filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <Column field="customer.mobile" :header="$t('mobile')" filterField="customerMobile" sortable>
        <template #body="slotProps">
          <a
            @click.prevent="startTeamsCall(slotProps.data.customer.mobile)"
            href="#"
            target="_blank"
          >
            {{ slotProps.data.customer.mobile }}
          </a>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <Column field="customer.email" :header="$t('email')" filterField="customerEmail">
        <template #body="slotProps">
          <a :href="`mailto:${slotProps.data.customer.email}`" target="_blank">{{
            slotProps.data.customer.email
          }}</a>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <Column field="sales.notes" :header="$t('notes')" sortable filterField="salesNotes">
        <template #body="slotProps">
          <span v-html="slotProps.data.sales.notes"></span>
        </template>
        <template #editor="slotProps">
          <InputText v-model="slotProps.data[slotProps.column.field]" />
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <Column
        field="sales.phase"
        :header="$t('phase')"
        :showFilterMenu="false"
        :styles="{ width: '7%' }"
      >
        <template #body="slotProps">
          <b-badge
            v-if="slotProps.data.sales && slotProps.data.sales.phase"
            :variant="getSalesPhaseColor(slotProps.data.sales.phase)"
          >
            {{ $t(slotProps.data.sales.phase) }}
          </b-badge>
        </template>

        <template #editor="{ data, field }">
          <Dropdown
            v-model="data[field]"
            :options="getEnumValues('SalesPhase')"
            :placeholder="$t('select')"
          >
            <template #option="slotProps">
              <b-badge :variant="getSalesPhaseColor(slotProps.option)">
                {{ $t(slotProps.option) }}
              </b-badge>
            </template>
            <template #value="slotProps">
              <b-badge :variant="getSalesPhaseColor(slotProps.value)">
                {{ $t(slotProps.value) }}
              </b-badge>
            </template>
          </Dropdown>
        </template>

        <template #filter>
          <MultiSelect
            :value="tableStateStore.customFilters.salesPhases"
            :options="salesPhases"
            @input="onSalesPhaseFilter"
            optionLabel="label"
            :placeholder="$t('phase')"
            display="chip"
            :style="{ 'max-width': '100px' }"
          />
        </template>
      </Column>
      <Column
        field="sales.callState"
        :header="$t('callState')"
        :showFilterMenu="false"
        :styles="{ width: '7%' }"
      >
        <template #body="slotProps">
          <b-badge
            v-if="slotProps.data.sales && slotProps.data.sales.callState"
            :variant="getCallStateColor(slotProps.data.sales.callState)"
          >
            {{ $t(slotProps.data.sales.callState) }}
          </b-badge>
        </template>

        <template #editor="{ data, field }">
          <Dropdown
            v-model="data[field]"
            :options="getEnumValues('SalesCallState')"
            :placeholder="$t('select')"
          >
            <template #option="slotProps">
              <b-badge :variant="getCallStateColor(slotProps.option)">
                {{ $t(slotProps.option) }}
              </b-badge>
            </template>
            <template #value="slotProps">
              <b-badge :variant="getCallStateColor(slotProps.value)">
                {{ $t(slotProps.value) }}
              </b-badge>
            </template>
          </Dropdown>
        </template>

        <template #filter>
          <MultiSelect
            :value="tableStateStore.customFilters.callStates"
            :options="callStates"
            @input="onCallStateFilter"
            optionLabel="label"
            :placeholder="$t('callState')"
            display="chip"
            :style="{ 'max-width': '100px' }"
          />
        </template>
      </Column>

      <Column
        field="sales.inCharge"
        :header="$t('inCharge')"
        sortable
        filterField="salesInChargeSearch"
      >
        <template #body="slotProps">
          <span v-if="slotProps.data.sales && slotProps.data.sales.inCharge">
            {{ slotProps.data.sales.inCharge.firstname }}
            {{ slotProps.data.sales.inCharge.lastname }}
            <br />
            <small>{{ slotProps.data.sales.inCharge.email }}</small>
          </span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <!-- <Column field="sales.kwhprice" :header="$t('kwhprice')" filterField="kwhprice" sortable>
        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column> -->
      <!-- 
      <Column field="sales.usage" :header="$t('usage')" filterField="usage" sortable>
        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column> -->
    </DataTable>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import InputText from 'primevue/inputtext';
import MultiSelect from 'primevue/multiselect';
import { getYesNoStateColor, getSalesPhaseColor, getCallStateColor } from '@/helpers/colors';
import { generateGoogleMapsLink, startTeamsCall } from '@/helpers/integration';
import { hasResourcePermission } from '@/helpers/userCapabilities';
import { yesNo } from '@/helpers/enums';

export default {
  components: {
    DataTable,
    Column,
    InputText,
    MultiSelect,
  },
  props: {
    phase: {
      type: String,
      required: false,
    },
    cardTitle: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      salesTableStateConfig: {
        name: `sales-table`,
        filters: {
          number: { value: null },
          clientProjectNumber: { value: null },
          customerLastname: { value: null },
          customerFirstname: { value: null },
          customerZip: { value: null },
          customerStreet: { value: null },
          customerCity: { value: null },
          customerPhone: { value: null },
          customerMobile: { value: null },
          customerEmail: { value: null },
          employerName: { value: null },
          installationAt: { value: null },
          solarPlantOverallPower: {
            value: null,
          },
          itemPlanningFinishedState: {
            value: null,
            label: null,
          },
          calendarWeek: { value: null },
          guaranteeCode: { value: null },
          requiredInstallationTimeinHours: { value: null },
          appointmentApprovedState: { value: null },
          installationDescription: { value: null },
          salesNotes: { value: null },
          salesInChargeSearch: { value: null },
        },
        customFilters: {
          employers: [],
          clients: [],
          client: null,
          installationTeams: [],
          installationTypes: [],
          salesPhases: [],
          callStates: [],
          projectStates: [],
        },
        sorting: { sortField: 'number', sortOrder: -1 },
      },
      defaultFilters: {
        number: { value: null },
        clientProjectNumber: { value: null },
        customerLastname: { value: null },
        customerFirstname: { value: null },
        customerZip: { value: null },
        customerStreet: { value: null },
        customerCity: { value: null },
        customerPhone: { value: null },
        customerMobile: { value: null },
        customerEmail: { value: null },
        employerName: { value: null },
        installationAt: { value: null },
        solarPlantOverallPower: {
          value: null,
        },
        itemPlanningFinishedState: { value: null, label: null },
        calendarWeek: { value: null },
        guaranteeCode: { value: null },
        requiredInstallationTimeinHours: { value: null },
        appointmentApprovedState: { value: null },
        installationDescription: { value: null },
      },
      defaultCustomFilters: {
        employers: [],
        clients: [],
        client: null,
        installationTeams: [],
        installationTypes: [],
        salesPhases: [],
        callStates: [],
      },
    };
  },
  computed: {
    ...mapGetters('auth', ['isAdmin', 'getCurrentUser', 'isClient']),
    ...mapGetters([
      'isLoading',
      'getProjects',
      'getProjectCount',
      'getEmployers',
      'getClients',
      'getEnumValues',
      'getTableState',
    ]),

    tableStateStore() {
      return this.getTableState(this.salesTableStateConfig.name);
    },

    salesPhases() {
      const salesPhases = this.getEnumValues('SalesPhase').map((salesPhase) => {
        return { value: salesPhase, label: this.$t(salesPhase) };
      });
      return salesPhases;
    },
    callStates() {
      const callStates = this.getEnumValues('SalesCallState').map((callState) => {
        return { value: callState, label: this.$t(callState) };
      });
      return callStates;
    },
    projects() {
      return this.getProjects;
    },
    pageOffset() {
      return this.tableStateStore.pagination.page * this.tableStateStore.pagination.pageSize;
    },
    employers() {
      if (!this.tableStateStore.customFilters.client) return [];

      const employers = this.getEmployers
        .filter(
          (employer) => employer.client.id === this.tableStateStore.customFilters.client.value
        )
        .map((employer) => {
          return { value: employer.id.toString(), label: employer.name };
        });
      return employers;
    },
    clients() {
      const clients = this.getClients.map((client) => {
        return { value: client.id.toString(), label: client.name };
      });
      return clients;
    },

    installationTeams() {
      const installationTeams = this.getEnumValues('InstallationTeam').map((installationTeam) => {
        return { value: installationTeam, label: this.$t(installationTeam) };
      });
      return installationTeams;
    },
    installationTypes() {
      const installationTypes = this.getEnumValues('InstallationType').map((installationType) => {
        return { value: installationType, label: this.$t(installationType) };
      });
      return installationTypes;
    },
    optionsYesNo() {
      return yesNo().map((option) => {
        return { value: option, label: this.$t(option) };
      });
    },

    /**
     * Disable employer dropdown if no client is selected
     */
    employerDropdownDisabled() {
      return this.tableStateStore.customFilters.client ? false : true;
    },
  },
  methods: {
    ...mapActions([
      'fetchProjectsPaginated',
      'fetchProjectsPaginatedByClient',
      'fetchEmployers',
      'fetchClients',
      'fetchEmployersByClient',
      'fetchEnumValues',
      'initTableState',
    ]),

    async onSalesPhaseFilter(value) {
      const filteredSalesPhases = {
        salesPhases: this.salesPhases.filter((salesPhase) => value.includes(salesPhase)),
      };
      this.tableStateStore.setCustomFilters(filteredSalesPhases);
      await this.loadTableData();
    },
    async onCallStateFilter(value) {
      const filteredCallStates = {
        callStates: this.callStates.filter((callState) => value.includes(callState)),
      };
      this.tableStateStore.setCustomFilters(filteredCallStates);
      await this.loadTableData();
    },
    /**
     * Load remote table data
     */
    async loadTableData() {
      const filters = { ...this.tableStateStore.filters, ...this.tableStateStore.customFilters };

      // Add fixed filter for projectStates "lead"
      filters.projectStates = [{ value: 'PROJECT_STATE_LEAD' }];

      if (this.isClient) {
        await this.fetchProjectsPaginatedByClient({
          clientId: this.getCurrentUser.client.id,
          page: this.pageOffset,
          pageSize: this.tableStateStore.pagination.pageSize,
          sortField: this.tableStateStore.sorting.sortField,
          sortOrder: this.tableStateStore.sorting.sortOrder,
          filters: filters,
        });
      } else {
        await this.fetchProjectsPaginated({
          page: this.pageOffset,
          pageSize: this.tableStateStore.pagination.pageSize,
          sortField: this.tableStateStore.sorting.sortField,
          sortOrder: this.tableStateStore.sorting.sortOrder,
          filters: filters,
        });
      }
      await this.fetchEnumValues('SalesCallState');
    },

    /**
     * Load more results from server or cache on pagination click
     */
    async onPage(event) {
      this.tableStateStore.pagination = { page: event.page, pageSize: event.rows };
      this.loadTableData();
    },
    /**
     * Load results from server/cache on sort
     */
    async onSort(event) {
      this.tableStateStore.sorting = { sortField: event.sortField, sortOrder: event.sortOrder };
      this.loadTableData();
    },
    /**
     * Load results from server/cache on filter
     */
    async onFilter(event) {
      this.loadTableData();
    },
    async onRemoveFilters(event) {
      this.tableStateStore.reset();
      await this.loadTableData();
    },
    async onStateRestore(event) {
      this.tableStateStore.restore(event);
    },

    /**
     *
     */
    async onEmployerFilter(value) {
      const filteredEmployers = {
        employers: this.employers.filter((employer) => value.includes(employer)),
      };
      this.tableStateStore.setCustomFilters(filteredEmployers);
      await this.loadTableData();
    },

    /**
     *
     */
    async onClientFilter(value) {
      const clientFilters = {
        employers: [],
        client: value,
      };
      this.tableStateStore.setCustomFilters(clientFilters);
      await this.loadTableData();
    },
    async onCellEditComplete(event) {
      let { data, newValue, field } = event;
      // console.log('onCellEditComplete():', event);
      // console.log(data, newValue, field);
      data[field] = newValue;

      this.save(data);
    },

    /**
     *
     */
    async onInstallationTeamFilter(value) {
      const filteredInstallationTeams = {
        installationTeams: this.installationTeams.filter((installationTeam) =>
          value.includes(installationTeam)
        ),
      };
      this.tableStateStore.setCustomFilters(filteredInstallationTeams);
      await this.loadTableData();
    },

    /**
     *
     */
    async onInstallationTypeFilter(value) {
      const filteredInstallationTypes = {
        installationTypes: this.installationTypes.filter((installationType) =>
          value.includes(installationType)
        ),
      };
      this.tableStateStore.setCustomFilters(filteredInstallationTypes);
      await this.loadTableData();
    },

    getYesNoStateColor,
    getSalesPhaseColor,
    getCallStateColor,
    yesNo,
    startTeamsCall,
    generateGoogleMapsLink,
    hasResourcePermission,
  },
  async created() {
    await this.initTableState(this.salesTableStateConfig);
    if (this.phase) {
      this.tableStateStore.customFilters.salesPhases = [this.phase];
    }
  },
  async mounted() {
    await this.fetchEnumValues('InstallationTeam');
    await this.fetchEnumValues('InstallationType');
    await this.fetchEnumValues('SalesPhase');

    if (this.isClient) {
      await this.fetchEmployersByClient(this.getCurrentUser.client.id);
      this.tableStateStore.customFilters.client = {
        value: this.getCurrentUser.client.id.toString(),
        label: this.getCurrentUser.client.name,
      };
    } else if (this.isAdmin) {
      await this.fetchEmployers();
      await this.fetchClients();
    }
    await this.loadTableData();

    if (this.phase) {
      const phaseFilterValue = this.salesPhases.find((sp) => sp.value === this.phase);
      this.onSalesPhaseFilter([phaseFilterValue]);
      this.tableStateStore.customFilters.salesPhases = [{ value: this.phase }];
      await this.loadTableData();
    }
  },
};
</script>

<style lang="scss">
.p-column-filter-menu-button {
  display: none;
}
.p-column-filter-clear-button {
  display: none;
}
.table-header {
  display: flex;
  justify-content: space-between;
}
.table-header-left {
  display: flex;
  justify-content: flex-start;
}
.table-header-right {
  display: flex;
  justify-content: flex-end;
  column-gap: 1em;
}

.p-calendar .p-datepicker {
  min-width: 100%;
  width: 29em;
}

/** Fix Dropdown overlay */
.p-datatable-responsive-scroll > .p-datatable-wrapper {
  min-height: 37rem;
}
</style>
